import React from 'react';
import { Maths } from 'helpers/maths';
import useBoundingRectBySelector from 'screens/Applications&WorkOrders/Components/DetailsTabs/hooks/useBoundingRectBySelector';
import { OPERATION_DETAILS_TABS_WRAPPER_SELECTOR } from 'screens/Applications&WorkOrders/Components/DetailsTabs/constants';

export const NOT_AVAILABLE = '---';

// Matches `useTableScrollConfig`'s own `TABS_HEADER_HEIGHT_PX` reserve for
// the antd `Tabs` nav bar sitting above the measured wrapper's content.
const TABS_NAV_HEIGHT_PX = 40;
const TAB_CONTENT_BOTTOM_GAP_PX = 16;
// Used only until the wrapper's rect resolves (or in a non-DOM environment,
// e.g. SSR/tests) — the same fixed guess this hook replaces for the steady
// state, since a stale-but-plausible height is safer than an unbounded one.
const TAB_CONTENT_FALLBACK_MAX_HEIGHT = 'calc(100vh - 373px)';

/**
 * Bounds each non-table tab's content to the viewport so it scrolls
 * internally instead of growing the page past it. Measures the shared
 * wrapper's live position rather than a hardcoded `calc(100vh - Npx)` — the
 * hardcoded value under-measured Plans' shorter, single-row header.
 */
export const usePlanTabContentStyle = (): React.CSSProperties => {
  const wrapperRect = useBoundingRectBySelector(
    OPERATION_DETAILS_TABS_WRAPPER_SELECTOR
  );
  const hasWindow = globalThis.window !== undefined;

  const maxHeight =
    wrapperRect && hasWindow
      ? `${Math.max(
          globalThis.window.innerHeight -
            wrapperRect.top -
            TABS_NAV_HEIGHT_PX -
            TAB_CONTENT_BOTTOM_GAP_PX,
          0
        )}px`
      : TAB_CONTENT_FALLBACK_MAX_HEIGHT;

  return {
    maxHeight,
    overflowY: 'auto',
    clipPath: 'inset(0 round 8px)'
  };
};

/**
 * One label/value row, matching the Applications `DetailOverview`/Invoices
 * `InfoRow` rows (fixed-width neutral label + value). `compact` selects the
 * narrower overview-card label width (Figma "Work Order Key Inputs V2", 81px)
 * used by the header stat cards, instead of the wider `details-field-label`
 * used by the tab content below.
 */
export const InfoRow: React.FC<{
  label: string;
  children: React.ReactNode;
  compact?: boolean;
  className?: string;
  labelStyle?: React.CSSProperties;
}> = ({ label, children, compact = false, className = '', labelStyle }) => (
  <div
    className={`details-field-row flex items-start gap-5 py-2 border-b border-neutral-20 leading-5 min-w-0 ${className}${
      compact ? ' overview-field-row' : ''
    }`}
  >
    <div
      className={`text-sm text-neutral-60 shrink-0${
        compact
          ? ' overview-field-label'
          : ' details-field-label plan-details-field-label'
      }`}
      style={labelStyle}
    >
      {label}
    </div>
    <div className="text-sm text-neutral-1000 min-w-0 flex-1">{children}</div>
  </div>
);

/** Mirrors `FixedCostSection`'s `getPerUnitMath` — a fixed cost field's per-acre value times Planned Area. */
export const perUnitTotal = (
  perAcreValue?: number,
  areaValue?: number
): number => {
  const result = Maths.multiplyRound(areaValue ?? 0, perAcreValue ?? 0, 2);
  return Math.max(result, 0);
};

/** Mirrors `ProjectedRevenueSection`'s `getPerAcreMath` — a total value divided by Planned Area. */
export const perAcreOfTotal = (
  totalValue?: number,
  areaValue?: number
): number => {
  const result = Maths.divideSafeRound(totalValue ?? 0, areaValue ?? 0, 2);
  return Math.max(result, 0);
};

/** Planned Area, rounded to 2 decimal places — matches `PlanRowItem`'s own formatting. */
export const formatArea = (value?: number): string =>
  value != null ? value.toFixed(2) : NOT_AVAILABLE;

/** Untyped legacy JS's `usePlanData` return; only the fields the detail panel and its tabs read. */
export interface PlanDetails {
  id?: string;
  name?: string;
  notes?: string;
  createdDateEpoch?: number;
  cropId?: string;
  cropSeasonIds?: string[];
  flagIds?: string[];
  estimatedAreaValue?: number;
  estimatedAreaUnit?: string;
  fixedCost?: import('../types').FixedCostValues;
  projectedRevenue?: import('../types').ProjectedRevenueValues;
  cropZones?: Array<{
    cropZoneIdValue: string;
    areaValue?: number;
    areaUnit?: string;
    coveragePct?: number;
  }>;
  planProductEntries?: import('../types').PlanProductLineItem[];
}
